// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 4f5fa697-c0ce-47fd-affe-1d408d8ff74c

import React, { useEffect, useMemo, useState } from 'react';
import ReactFlow, { Background, Controls, MarkerType, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box, Paper, Stack, Typography, TextField, Button, Chip, Checkbox, FormControlLabel,
  FormGroup, Alert, AlertTitle, Divider, InputAdornment, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { FACT_SHEET_TYPES, RELATION_TYPES, typeMeta, relationMeta } from './schema';
import { printElementToPdf } from './exports';
import { buildNameIndex, lookupName } from './nameIndex';
import store from './store';

// ---------------------------------------------------------------------------
// Portfolio Mind Map — the whole estate on one canvas.
//
// Every other graph in the app is per-fact-sheet (RelationsExplorer = one hub,
// IntegrationFlow = one app's hops). This is the portfolio-wide view: all fact
// sheets, all model relations, plus the integration data flows overlaid as
// dashed edges.
//
// Layout is deterministic and computed here — no force simulation, no layout
// library, no animation loop. Applications form a grid at the centre (they are
// the densest type and the thing everyone looks for); every other type gets an
// angular wedge on the surrounding rings, sized by how many nodes it has.
//
// Inventories run 400–4,000 fact sheets. Rendering that many SVG nodes wedges
// the browser, so anything over MAX_NODES is refused until the user narrows the
// filters or opts into "top N by connection count".
// ---------------------------------------------------------------------------

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const MAX_NODES = 300;          // hard render ceiling
const LABEL_LIMIT = 90;         // above this many nodes, drop edge labels
const VIRTUALISE_ABOVE = 120;   // above this, only render what's on screen
const TABLE_LIMIT = 200;        // rows in the printable connection table

// Pseudo relation key for integration ("data flow") edges, so integrations can
// be filtered alongside the model relations in the same checkbox list.
const INTEGRATION_KEY = '__integration';

// Ring order, innermost first. Applications sit in the middle (see layoutNodes).
const RING_ORDER = [
  'BusinessCapability', 'Interface', 'DataObject', 'ITComponent', 'Organization',
  'Platform', 'BusinessContext', 'Provider', 'TechCategory', 'Initiative',
  'Objective', 'Contract',
];

const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();

// ---------------------------------------------------------------------------
// Deterministic layout: centre grid of Applications + concentric type wedges.
// Same input set always yields the same coordinates.
// ---------------------------------------------------------------------------
function layoutNodes(sheets) {
  const pos = new Map();
  const apps = sheets.filter((s) => s.type === 'Application');
  const rest = sheets.filter((s) => s.type !== 'Application');

  const CX = 235;
  const CY = 96;
  const cols = Math.max(1, Math.ceil(Math.sqrt(apps.length)));
  const rows = Math.max(1, Math.ceil(apps.length / cols));
  apps.forEach((s, i) => {
    const r = Math.floor(i / cols);
    const c = i % cols;
    pos.set(s.id, { x: (c - (cols - 1) / 2) * CX, y: (r - (rows - 1) / 2) * CY });
  });

  // Radius that clears the Application cluster.
  const appR = Math.max(260, Math.max(cols * CX, rows * CY) / 2 + 130);

  // Group the remaining types in a fixed order so the rings never reshuffle.
  const groups = [];
  RING_ORDER.forEach((t) => {
    const g = rest.filter((s) => s.type === t);
    if (g.length) groups.push(g);
  });
  const known = new Set(RING_ORDER);
  const unknown = rest.filter((s) => !known.has(s.type));
  if (unknown.length) groups.push(unknown);

  const total = rest.length || 1;
  let angle = -Math.PI / 2;
  groups.forEach((g) => {
    const span = (2 * Math.PI) * (g.length / total);
    const perRow = Math.max(3, Math.round((span / (2 * Math.PI)) * 30));
    g.forEach((s, i) => {
      const row = Math.floor(i / perRow);
      const col = i % perRow;
      const rad = appR + 160 + row * 150;
      const a = angle + ((col + 0.5) / perRow) * span;
      pos.set(s.id, { x: Math.cos(a) * rad * 1.5, y: Math.sin(a) * rad });
    });
    angle += span;
  });

  return pos;
}

export default function MindMap({ tenantId, onOpen }) {
  const [sheets, setSheets] = useState(null);
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set());
  const [hiddenRels, setHiddenRels] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [focusId, setFocusId] = useState(null);
  const [hideOrphans, setHideOrphans] = useState(true);
  const [topN, setTopN] = useState(false);

  useEffect(() => {
    let live = true;
    setSheets(null);
    store.listFactSheets(tenantId).then((rows) => { if (live) setSheets(rows || []); });
    return () => { live = false; };
  }, [tenantId]);

  // Reset transient view state when the tenant changes.
  useEffect(() => {
    setFocusId(null);
    setTopN(false);
    setSearch('');
    setHiddenTypes(new Set());
    setHiddenRels(new Set());
  }, [tenantId]);

  // ---- Full graph (unfiltered): every node, every candidate edge -----------
  const full = useMemo(() => {
    const rows = sheets || [];
    const byId = new Map();
    // Tenant-scoped: in the master workspace `rows` spans every client, and a
    // bare-name key drew edges from one client's application into another's.
    const byName = buildNameIndex(rows, { firstWins: true });
    rows.forEach((fs) => { byId.set(fs.id, fs); });

    const edges = [];
    const seen = new Set();
    const push = (source, target, relKey, label) => {
      if (!source || !target || source === target) return;
      const key = `${source}|${target}|${relKey}`;
      if (seen.has(key)) return;
      seen.add(key);
      edges.push({ id: `e${edges.length}`, source, target, relKey, label: label || '' });
    };

    rows.forEach((fs) => {
      // 1. Model relations: relations[relKey] = [{ id }]
      Object.entries(fs.relations || {}).forEach(([relKey, arr]) => {
        (arr || []).forEach((r) => {
          if (r && r.id && byId.has(r.id)) {
            push(fs.id, r.id, relKey, (relationMeta(relKey) || {}).label || relKey);
          }
        });
      });
      // 2. Integrations: match `to` / `via` against fact sheet names (case-
      //    insensitive). Unmatched endpoints (e.g. an external SaaS that has no
      //    fact sheet yet) are simply skipped — this map only draws the estate.
      (fs.integrations || []).forEach((it) => {
        if (!it) return;
        const to = lookupName(byName, fs, it.to);
        const via = lookupName(byName, fs, it.via);
        const hop = [it.protocol, it.dataType].filter(Boolean).join(' · ');
        if (via && to) {
          push(fs.id, via.id, INTEGRATION_KEY, hop);
          push(via.id, to.id, INTEGRATION_KEY, it.dataType || '');
        } else if (via) {
          push(fs.id, via.id, INTEGRATION_KEY, hop);
        } else if (to) {
          push(fs.id, to.id, INTEGRATION_KEY, hop);
        }
      });
    });

    const typeCounts = new Map();
    rows.forEach((fs) => typeCounts.set(fs.type, (typeCounts.get(fs.type) || 0) + 1));
    const relCounts = new Map();
    edges.forEach((e) => relCounts.set(e.relKey, (relCounts.get(e.relKey) || 0) + 1));

    return { rows, byId, edges, typeCounts, relCounts };
  }, [sheets]);

  // Types present, in schema order, then anything unexpected.
  const presentTypes = useMemo(() => {
    const keys = FACT_SHEET_TYPES.map((t) => t.key).filter((k) => full.typeCounts.has(k));
    const extra = [...full.typeCounts.keys()].filter((k) => !keys.includes(k));
    return [...keys, ...extra];
  }, [full]);

  // Relation keys present, in schema order, integrations last.
  const presentRels = useMemo(() => {
    const keys = RELATION_TYPES.map((r) => r.key).filter((k) => full.relCounts.has(k));
    const extra = [...full.relCounts.keys()].filter(
      (k) => k !== INTEGRATION_KEY && !keys.includes(k)
    );
    const out = [...keys, ...extra];
    if (full.relCounts.has(INTEGRATION_KEY)) out.push(INTEGRATION_KEY);
    return out;
  }, [full]);

  // ---- Filtered + laid out + styled graph ---------------------------------
  const view = useMemo(() => {
    const empty = {
      nodes: [], edges: [], nodeCount: 0, edgeCount: 0,
      overLimit: false, candidateCount: 0, truncated: false, rows: [], rowsTotal: 0,
    };
    if (!sheets || !full.rows.length) return empty;

    const typeOk = (t) => !hiddenTypes.has(t);
    const relOk = (k) => !hiddenRels.has(k);

    // 1. Type filter.
    let candidates = full.rows.filter((fs) => typeOk(fs.type));
    const candidateIds = new Set(candidates.map((fs) => fs.id));

    // 2. Relation filter — an edge survives only if both ends are still visible.
    let edges = full.edges.filter(
      (e) => relOk(e.relKey) && candidateIds.has(e.source) && candidateIds.has(e.target)
    );

    // 3. Focus mode — the clicked node plus its immediate neighbours only.
    const focused = focusId && candidateIds.has(focusId) ? focusId : null;
    if (focused) {
      edges = edges.filter((e) => e.source === focused || e.target === focused);
      const keep = new Set([focused]);
      edges.forEach((e) => { keep.add(e.source); keep.add(e.target); });
      candidates = candidates.filter((fs) => keep.has(fs.id));
    }

    // 4. Degree (used for orphan pruning and for "top N by connections").
    const degree = new Map();
    edges.forEach((e) => {
      degree.set(e.source, (degree.get(e.source) || 0) + 1);
      degree.set(e.target, (degree.get(e.target) || 0) + 1);
    });

    if (hideOrphans && !focused) {
      candidates = candidates.filter((fs) => (degree.get(fs.id) || 0) > 0);
    }

    // 5. Performance guard. Refuse to render a wall of nodes; make the user
    //    narrow the filters or explicitly opt into the top-N slice.
    const candidateCount = candidates.length;
    let truncated = false;
    if (candidateCount > MAX_NODES) {
      if (!topN) {
        return { ...empty, overLimit: true, candidateCount, edgeCount: edges.length };
      }
      candidates = [...candidates]
        .sort((a, b) => {
          const d = (degree.get(b.id) || 0) - (degree.get(a.id) || 0);
          return d !== 0 ? d : String(a.name).localeCompare(String(b.name));
        })
        .slice(0, MAX_NODES);
      truncated = true;
    }

    const visible = new Set(candidates.map((fs) => fs.id));
    if (truncated) edges = edges.filter((e) => visible.has(e.source) && visible.has(e.target));

    // 6. Deterministic layout over exactly the visible set.
    const pos = layoutNodes(candidates);
    const showLabels = candidates.length <= LABEL_LIMIT;

    // 7. Search: highlight matches, dim everything else.
    const q = norm(search);
    const matches = new Set();
    if (q) {
      candidates.forEach((fs) => {
        const hay = `${fs.name} ${(fs.fields && fs.fields.description) || ''}`.toLowerCase();
        if (hay.includes(q)) matches.add(fs.id);
      });
    }
    const dimmed = (id) => Boolean(q) && !matches.has(id);

    const rfNodes = candidates.map((fs) => {
      const m = typeMeta(fs.type);
      const isFocus = fs.id === focused;
      const isMatch = Boolean(q) && matches.has(fs.id);
      return {
        id: fs.id,
        position: pos.get(fs.id) || { x: 0, y: 0 },
        data: { label: `${m.badge} · ${fs.name}` },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: isFocus ? m.color : '#fff',
          color: isFocus ? '#fff' : NAVY,
          border: `2px solid ${m.color}`,
          borderRadius: 8,
          width: 175,
          fontSize: 11,
          fontWeight: isFocus || isMatch ? 700 : 500,
          padding: '6px 8px',
          opacity: dimmed(fs.id) ? 0.12 : 1,
          boxShadow: isMatch ? '0 0 0 3px rgba(245,166,35,0.9)' : 'none',
          cursor: 'pointer',
        },
      };
    });

    const rfEdges = edges.map((e) => {
      const isFlow = e.relKey === INTEGRATION_KEY;
      const faded = dimmed(e.source) && dimmed(e.target);
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        type: 'straight',
        label: showLabels ? e.label : undefined,
        labelStyle: { fontSize: 9, fill: GREY },
        labelBgStyle: { fill: '#fff', fillOpacity: 0.8 },
        markerEnd: { type: MarkerType.ArrowClosed, color: isFlow ? '#EF6C00' : '#9aa7b4' },
        style: {
          stroke: isFlow ? '#EF6C00' : '#9aa7b4',
          strokeWidth: isFlow ? 1.5 : 1,
          strokeDasharray: isFlow ? '5 4' : undefined,
          opacity: faded ? 0.08 : 0.85,
        },
      };
    });

    // 8. Printable connection list (the PDF's real payload — a ReactFlow canvas
    //    does not survive innerHTML printing, a table does).
    const nameOf = (id) => {
      const fs = full.byId.get(id);
      return fs ? fs.name : id;
    };
    const rowsAll = edges.map((e) => ({
      id: e.id,
      source: nameOf(e.source),
      target: nameOf(e.target),
      relation: e.relKey === INTEGRATION_KEY
        ? `Data flow${e.label ? ` (${e.label})` : ''}`
        : (relationMeta(e.relKey) || {}).label || e.relKey,
    }));

    return {
      nodes: rfNodes,
      edges: rfEdges,
      nodeCount: rfNodes.length,
      edgeCount: rfEdges.length,
      overLimit: false,
      candidateCount,
      truncated,
      rows: rowsAll.slice(0, TABLE_LIMIT),
      rowsTotal: rowsAll.length,
    };
  }, [sheets, full, hiddenTypes, hiddenRels, focusId, hideOrphans, topN, search]);

  const toggle = (setter) => (key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleType = toggle(setHiddenTypes);
  const toggleRel = toggle(setHiddenRels);

  const relLabel = (key) => (key === INTEGRATION_KEY
    ? 'Integrations (data flows)'
    : (relationMeta(key) || {}).label || key);

  const focusName = focusId && full.byId.get(focusId) ? full.byId.get(focusId).name : null;

  // Remount the canvas when the rendered set changes so fitView re-runs.
  const canvasKey = `${view.nodeCount}:${view.edgeCount}:${focusId || ''}:${topN ? 1 : 0}`;

  if (!sheets) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={22} />
        <Typography sx={{ color: GREY }}>Building the portfolio mind map…</Typography>
      </Box>
    );
  }

  // Empty state: nothing in the estate is connected to anything.
  if (!full.edges.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD, mb: 2 }}>
          Portfolio Mind Map
        </Typography>
        <Paper sx={{ p: 3, maxWidth: 720 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>No relationships captured yet</AlertTitle>
            {full.rows.length
              ? `${full.rows.length} fact sheet${full.rows.length === 1 ? '' : 's'} in this workspace, but none of them reference each other yet.`
              : 'There are no fact sheets in this workspace yet.'}
          </Alert>
          <Typography sx={{ fontSize: 13, color: GREY, mb: 1 }}>
            The mind map is drawn from two sources, both of which live on the fact sheet itself:
          </Typography>
          <Typography component="ul" sx={{ fontSize: 13, color: GREY, pl: 3, m: 0 }}>
            <li>
              <b>Relations</b> — open a fact sheet and add Business Capabilities, IT Components,
              Data Objects, Interfaces, Organizations and so on in its relation sections.
            </li>
            <li>
              <b>Integrations</b> — an application&apos;s integration rows become dashed data-flow
              edges wherever the <i>Connects To</i> or <i>Via</i> value matches another fact sheet
              by name.
            </li>
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Portfolio Mind Map</Typography>
        <Chip
          size="small"
          label={`${view.nodeCount} nodes · ${view.edgeCount} connections`}
          sx={{ bgcolor: '#e8f0fe', color: NAVY, fontWeight: 600 }}
        />
        {focusName && (
          <Chip
            size="small"
            color="primary"
            label={`Focus: ${focusName}`}
            onDelete={() => setFocusId(null)}
            deleteIcon={<ClearIcon />}
            sx={{ bgcolor: BLUE }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {focusId && (
          <Button size="small" startIcon={<ClearIcon />} onClick={() => setFocusId(null)}>
            Clear focus
          </Button>
        )}
        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          onClick={() => printElementToPdf('mindmap-print', 'Primordia — Portfolio Mind Map')}
        >
          PDF
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* ---- Controls ---- */}
        <Paper sx={{ p: 1.5, width: { xs: '100%', md: 290 }, flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search fact sheets…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: GREY }} /></InputAdornment>
              ),
            }}
          />
          <Typography sx={{ fontSize: 11, color: GREY, mt: 0.5 }}>
            Click a node to focus it · double-click to open the fact sheet.
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, flex: 1 }}>Fact Sheet Types</Typography>
            <Button size="small" sx={{ minWidth: 0, fontSize: 11 }} onClick={() => setHiddenTypes(new Set())}>All</Button>
            <Button size="small" sx={{ minWidth: 0, fontSize: 11 }} onClick={() => setHiddenTypes(new Set(presentTypes))}>None</Button>
          </Stack>
          <FormGroup sx={{ maxHeight: 230, overflowY: 'auto' }}>
            {presentTypes.map((key) => {
              const m = typeMeta(key);
              return (
                <FormControlLabel
                  key={key}
                  sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={!hiddenTypes.has(key)}
                      onChange={() => toggleType(key)}
                      sx={{ py: 0.25, color: m.color, '&.Mui-checked': { color: m.color } }}
                    />
                  )}
                  label={`${m.label} (${full.typeCounts.get(key) || 0})`}
                />
              );
            })}
          </FormGroup>

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, flex: 1 }}>Connections</Typography>
            <Button size="small" sx={{ minWidth: 0, fontSize: 11 }} onClick={() => setHiddenRels(new Set())}>All</Button>
            <Button size="small" sx={{ minWidth: 0, fontSize: 11 }} onClick={() => setHiddenRels(new Set(presentRels))}>None</Button>
          </Stack>
          <FormGroup sx={{ maxHeight: 230, overflowY: 'auto' }}>
            {presentRels.map((key) => (
              <FormControlLabel
                key={key}
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                control={(
                  <Checkbox
                    size="small"
                    checked={!hiddenRels.has(key)}
                    onChange={() => toggleRel(key)}
                    sx={{ py: 0.25, color: key === INTEGRATION_KEY ? '#EF6C00' : BLUE, '&.Mui-checked': { color: key === INTEGRATION_KEY ? '#EF6C00' : BLUE } }}
                  />
                )}
                label={`${relLabel(key)} (${full.relCounts.get(key) || 0})`}
              />
            ))}
          </FormGroup>

          <Divider sx={{ my: 1.5 }} />

          <FormControlLabel
            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
            control={<Checkbox size="small" checked={hideOrphans} onChange={() => setHideOrphans((v) => !v)} sx={{ py: 0.25 }} />}
            label="Hide unconnected fact sheets"
          />
          <FormControlLabel
            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
            control={<Checkbox size="small" checked={topN} onChange={() => setTopN((v) => !v)} sx={{ py: 0.25 }} />}
            label={`Cap at top ${MAX_NODES} by connections`}
          />
        </Paper>

        {/* ---- Canvas ---- */}
        <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
          {view.overLimit ? (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={(
                <Button color="inherit" size="small" onClick={() => setTopN(true)}>
                  {`Show top ${MAX_NODES}`}
                </Button>
              )}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>Too many fact sheets to draw</AlertTitle>
              {`${view.candidateCount.toLocaleString()} fact sheets match the current filters — rendering that many nodes would lock up the browser. Narrow the type or connection filters, focus on a single fact sheet, or render only the ${MAX_NODES} most connected fact sheets.`}
            </Alert>
          ) : (
            <>
              {view.truncated && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {`Showing the ${MAX_NODES} most connected of ${view.candidateCount.toLocaleString()} matching fact sheets. Narrow the filters to see the rest.`}
                </Alert>
              )}
              {view.nodeCount === 0 ? (
                <Alert severity="info">No fact sheets match the current filters.</Alert>
              ) : (
                <Paper sx={{ height: 620, width: '100%', overflow: 'hidden' }}>
                  <ReactFlow
                    key={canvasKey}
                    nodes={view.nodes}
                    edges={view.edges}
                    fitView
                    minZoom={0.05}
                    maxZoom={2}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable
                    onlyRenderVisibleElements={view.nodeCount > VIRTUALISE_ABOVE}
                    zoomOnScroll={false}
                    panOnScroll={false}
                    preventScrolling={false}
                    onNodeClick={(_, node) => setFocusId(node.id)}
                    onNodeDoubleClick={(_, node) => { if (typeof onOpen === 'function') onOpen(node.id); }}
                    proOptions={{ hideAttribution: true }}
                  >
                    <Background gap={18} color="#eef1f5" />
                    <Controls showInteractive={false} />
                  </ReactFlow>
                </Paper>
              )}
            </>
          )}

          {/* ---- Legend + printable summary ---- */}
          <div id="mindmap-print">
            <Paper sx={{ p: 1.5, mt: 2 }}>
              <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14, mb: 0.5 }}>
                Portfolio Mind Map — {view.nodeCount} fact sheets, {view.edgeCount} connections
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {presentTypes.filter((k) => !hiddenTypes.has(k)).map((key) => {
                  const m = typeMeta(key);
                  return (
                    <Chip
                      key={key}
                      size="small"
                      label={`${m.badge} ${m.label}`}
                      sx={{ bgcolor: m.color, color: '#fff', height: 20, fontSize: 11 }}
                    />
                  );
                })}
                <Chip
                  size="small"
                  label="— — dashed = integration data flow"
                  sx={{ bgcolor: '#fff3e0', color: '#8a5a00', height: 20, fontSize: 11, border: '1px dashed #EF6C00' }}
                />
              </Stack>

              {view.rows.length > 0 && (
                <>
                  <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 12, mt: 1 }}>
                    Connections
                    {view.rowsTotal > view.rows.length ? ` (first ${view.rows.length} of ${view.rowsTotal})` : ''}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD, fontSize: 12 } }}>
                        <TableCell>From</TableCell>
                        <TableCell>Connection</TableCell>
                        <TableCell>To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {view.rows.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell sx={{ fontSize: 12 }}>{r.source}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: GREY }}>{r.relation}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.target}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Paper>
          </div>
        </Box>
      </Stack>
    </Box>
  );
}
